import { useEffect, useState, useRef } from "react";
import {
  Trophy, Gem, Crown, Flame, Zap, Gamepad2, Coins, Users, Disc3,
  Lock, Check, Copy, Share2, Gift, Loader2, Camera, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins, avatarUrl } from "../lib/format";
import { playReward, playClick } from "../lib/sound";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import InvitePoster from "../components/InvitePoster";
import VipCard from "../components/VipCard";
import { Link } from "react-router-dom";

const ICONS = { Trophy, Gem, Crown, Flame, Zap, Gamepad2, Coins, Users, Disc3 };
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Trophy; return <C {...p} />; };

export default function Profile() {
  const { user, setCoins, refresh } = useAuth();
  const { t, lang } = useLang();
  const [ach, setAch] = useState([]);
  const [missions, setMissions] = useState([]);
  const [ref, setRef] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [fx, setFx] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed ✓");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const loadAll = () => {
    api.get("/achievements").then(({ data }) => setAch(data)).catch(() => {});
    api.get("/missions").then(({ data }) => setMissions(data)).catch(() => {});
    api.get("/referral").then(({ data }) => setRef(data)).catch(() => {});
  };
  useEffect(() => { refresh(); loadAll(); }, []);

  const claim = async (m) => {
    setClaiming(m.id);
    try {
      const { data } = await api.post("/missions/claim", { mission_id: m.id });
      setCoins(data.coins);
      setFx(Date.now());
      playReward();
      toast.success(`+${formatCoins(data.reward)} ${t("coins")}!`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setClaiming(null);
    }
  };

  const refLink = ref ? `${window.location.origin}/login?ref=${ref.code}` : "";
  const copyCode = async () => {
    playClick();
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success(t("copied"));
    } catch {
      toast(refLink);
    }
  };
  const shareCode = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Bam Bam Bhole", text: t("inviteText"), url: refLink }); } catch {}
    } else copyCode();
  };

  const unlockedCount = ach.filter((a) => a.unlocked).length;

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("changePhoto") + " ✓");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      <Confetti fire={fx} />
      <CoinRain fire={fx} />

      {/* User card */}
      <div className="mw-card p-6 flex items-center gap-4">
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="avatar-upload-btn"
          className="relative group grid place-items-center h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400/30 to-purple-600/20 border border-white/10 font-display text-2xl font-bold overflow-hidden"
        >
          {user?.picture ? <img src={avatarUrl(user.picture)} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "?").toUpperCase()}
          <span className="absolute inset-0 grid place-items-center bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
            {uploading ? <Loader2 size={18} className="animate-spin text-amber-300" /> : <Camera size={18} className="text-amber-300" />}
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-file-input" />
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-extrabold truncate">{user?.name}</h1>
          <p className="text-sm text-slate-400 truncate">{user?.email}</p>
          <button onClick={() => fileRef.current?.click()} className="mt-1 text-xs text-cyan-300 hover:underline flex items-center gap-1">
            <Camera size={12} /> {uploading ? t("uploading") : t("changePhoto")}
          </button>
        </div>
        <div className="ml-auto text-right">
          <p className="flex items-center gap-1 text-amber-300 font-mono-num font-bold justify-end">
            <Coins size={15} /> {formatCoins(user?.coins)}
          </p>
        </div>
      </div>

      <VipCard onReward={() => setFx(Date.now())} />
      <Link to="/app/rules" data-testid="profile-rules-link" className="mw-card p-4 flex items-center justify-between text-sm font-semibold text-amber-200 hover:border-amber-400/60 transition-colors">
        {t("rulesTerms")} <span aria-hidden>→</span>
      </Link>

      {/* Refer & Earn */}
      <section className="mw-card p-6 relative overflow-hidden" data-testid="referral-card">
        <div className="absolute -right-6 -top-6 opacity-15"><Users size={120} className="text-cyan-400" /></div>
        <div className="relative z-10">
          <h2 className="font-display text-2xl font-bold">{t("referAndEarn")}</h2>
          <p className="text-sm text-slate-400 mt-1">{t("inviteBonus")}</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex-1 rounded-xl border border-amber-500/30 bg-black/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("yourCode")}</p>
              <p className="font-mono-num text-2xl font-extrabold gold-text tracking-widest" data-testid="referral-code">{ref?.code || "..."}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={copyCode} data-testid="referral-copy-btn" className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-black/30 px-4 py-3 text-sm font-bold text-cyan-300 hover:border-cyan-400/80 transition-colors">
                <Copy size={16} /> {t("copy")}
              </button>
              <button onClick={shareCode} data-testid="referral-share-btn" className="flex items-center gap-2 btn-gold rounded-xl px-4 py-3 text-sm">
                <Share2 size={16} /> {t("share")}
              </button>
            </div>
          </div>
          <div className="mt-3">
            <InvitePoster code={ref?.code} />
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200" data-testid="refer-earn-terms">
            <Gift size={15} className="shrink-0 mt-0.5" /> {t("referEarnTerms")}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 max-w-xs">
            <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-center">
              <p className="text-[10px] uppercase text-slate-400">{t("referrals")}</p>
              <p className="font-mono-num font-bold text-cyan-300">{ref?.count ?? 0}</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-center">
              <p className="text-[10px] uppercase text-slate-400">{t("earned")}</p>
              <p className="font-mono-num font-bold text-emerald-400">{formatCoins(ref?.earned ?? 0)}</p>
            </div>
          </div>
          {ref?.friends?.length > 0 && (
            <div className="mt-4" data-testid="referral-friends">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("invitedFriends")}</p>
              <div className="flex flex-wrap gap-2">
                {ref.friends.map((f, i) => (
                  <span key={i} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${f.status === "earned" ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-100" : "border-amber-400/30 bg-amber-500/10 text-amber-100"}`}>
                    <Users size={12} /> {f.name}
                    <span className="opacity-70">· {f.status === "earned" ? t("earned") : t("pending")}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Change Password */}
      <section className="mw-card p-6" data-testid="change-password-card">
        <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><KeyRound size={20} className="text-amber-400" /> {t("password")}</h2>
        <form onSubmit={changePassword} className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-slate-400">Current</label>
            <input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              data-testid="current-password-input" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-slate-400">New (min 6)</label>
            <input type="password" required minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              data-testid="new-password-input" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
          </div>
          <button type="submit" disabled={pwBusy} data-testid="change-password-btn" className="btn-gold rounded-lg px-5 py-2 flex items-center gap-2">
            {pwBusy && <Loader2 size={15} className="animate-spin" />} {t("password")}
          </button>
        </form>
      </section>

      {/* Achievements */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-bold">{t("achievements")}</h2>
          <span className="text-sm font-mono-num text-amber-300">{unlockedCount}/{ach.length}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="achievements-grid">
          {ach.map((a) => (
            <div
              key={a.id}
              data-testid={`achievement-${a.id}`}
              className={`mw-card p-4 flex flex-col items-center text-center gap-2 transition-all ${a.unlocked ? "mw-card-hover glow-gold" : "opacity-55 grayscale"}`}
            >
              <div className={`relative grid place-items-center h-14 w-14 rounded-2xl ${a.unlocked ? "bg-gradient-to-br from-amber-400/30 to-amber-600/10 text-amber-300" : "bg-black/40 text-slate-500"}`}>
                <Icon name={a.icon} size={26} />
                {!a.unlocked && <Lock size={12} className="absolute -bottom-1 -right-1 text-slate-400" />}
                {a.unlocked && <Check size={14} className="absolute -bottom-1 -right-1 text-emerald-400" />}
              </div>
              <p className="font-display font-bold text-sm">{lang === "hi" ? a.title_hi : a.title_en}</p>
              <p className="text-[11px] text-slate-400 leading-snug">{lang === "hi" ? a.desc_hi : a.desc_en}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
